
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import Head from 'next/head';
import Script from 'next/script';
import type { TypePropsGetLayout } from '@/components/Layout';
import type { TypeHeaderProps } from '@/molecules/header';
import type { TypeFooterProps } from '@/organisms/footer';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import type { ReactElement, ReactNode } from 'react';
import '@/styles/globals.css';
import { ErrorBoundary } from '@/molecules';
import { ErrorPage } from '@/templates';
// eslint-disable-next-line @typescript-eslint/ban-types
type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
    getLayout?: ({ children, header, footer }: TypePropsGetLayout) => ReactNode;
};
type AppPropsWithLayout<T> = AppProps<T> & {
    Component: NextPageWithLayout;
};
export type AppCommonProps = {
    seo: {
        title?: string;
        metaDesc?: string;
        metaKeywords?: string;
        keywords?: string;
        canonical?: string;
        metaRobotsNoindex?: string;
        metaRobotsNofollow?: string;
    };
    header: TypeHeaderProps;
    footer: TypeFooterProps;
};
declare global {
    interface Window {
        embeddedservice_bootstrap: any;
        __becasaChatInitialized?: boolean;
    }
}
const SALESFORCE_CHAT_ORG_ID = '00Dd3000001UJW6';
const SALESFORCE_CHAT_DEPLOYMENT_NAME = 'Be_Casa_Essential_Web_Chat';
const SALESFORCE_CHAT_BASE_URL = 'https://greystareuropeholdings.my.site.com/ESWBeCaseEssentialWeb1771865761405';
const SALESFORCE_CHAT_SCRT2_URL = 'https://greystareuropeholdings.my.salesforce-scrt.com';
const SALESFORCE_CHAT_BRAND = 'Be Casa Essential';
const App = ({ Component, pageProps: { seo, header, footer, ...others }, }: AppPropsWithLayout<AppCommonProps>) => {
    const getLayout = Component.getLayout
        ? (children: ReactElement) => Component.getLayout &&
            Component.getLayout({ children: children, header, footer })
        : (children: ReactElement) => children;
    let canonicalFinal;
    if (seo?.canonical) {
        const reg = /\/es\/$/;
        canonicalFinal = seo?.canonical?.replace('https://cmseditor.becasaessential.com/', 'https://www.becasaessential.com/');
        if (reg.test(seo?.canonical)) {
            canonicalFinal = canonicalFinal.replace(reg, '/');
        }
        else {
            canonicalFinal = canonicalFinal.slice(0, -1);
        }
    }
    const initEmbeddedMessaging = () => {
        if (typeof window === 'undefined')
            return;
        if (window.__becasaChatInitialized)
            return;
        if (!window.embeddedservice_bootstrap) {
            // eslint-disable-next-line no-console
            console.error('Embedded Messaging bootstrap not available');
            return;
        }
        try {
            window.__becasaChatInitialized = true;
            window.embeddedservice_bootstrap.settings.language = 'en_US';
            window.addEventListener('onEmbeddedMessagingReady', () => {
                // eslint-disable-next-line no-console
                console.log('Inside Prechat API!!');
                window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
                    Brand: SALESFORCE_CHAT_BRAND,
                });
            }, { once: true });
            window.embeddedservice_bootstrap.init(SALESFORCE_CHAT_ORG_ID, SALESFORCE_CHAT_DEPLOYMENT_NAME, SALESFORCE_CHAT_BASE_URL, {
                scrt2URL: SALESFORCE_CHAT_SCRT2_URL,
            });
        }
        catch (err) {
            window.__becasaChatInitialized = false;
            // eslint-disable-next-line no-console
            console.error('Error loading Embedded Messaging: ', err);
        }
    };
    return (<>
      <ErrorBoundary fallbackComponent={<ErrorPage statusCode={500}/>}>
        <Head>
          {seo?.title && <title>{seo.title}</title>}
          {seo?.metaDesc && <meta name="description" content={seo.metaDesc}/>}
          {(seo?.metaRobotsNoindex || seo?.metaRobotsNofollow) && (<meta name="robots" content={`${seo?.metaRobotsNoindex},${seo?.metaRobotsNofollow}`}/>)}
          {seo?.canonical && (<link rel="canonical" href={canonicalFinal ?? ''}/>)}
        </Head>
        <Script id="google-tag-manager-denied-consent" strategy="beforeInteractive">
          {`
                window.dataLayer = window.dataLayer || []; 
                function gtag(){dataLayer.push(arguments);} 
                // Default ad_storage to 'denied'. 
                gtag('consent', 'default', { 
                  ad_storage: "denied",
                  analytics_storage: "denied",
                  ad_user_data: "denied",
                  ad_personalization: "denied",
                  wait_for_update: 2000
                }); 
              `}
        </Script>

        {getLayout(<Component {...others}/>)}

        {process.env.GTM_ID && (<>
            <Script id="google-tag-manager" strategy="afterInteractive">
              {`
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${process.env.GTM_ID}');
              `}
            </Script>
          </>)}

        <Script id="salesforce-chat" type="text/javascript" strategy="afterInteractive" src={`${SALESFORCE_CHAT_BASE_URL}/assets/js/bootstrap.min.js`} onLoad={initEmbeddedMessaging}/>

        {process.env.COOKIE_LAW_ID && (<>
            <Script src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js" data-domain-script={process.env.COOKIE_LAW_ID} strategy="beforeInteractive"/>

            <Script id="cookie_view" 
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onLoad={() => { }} strategy="beforeInteractive">
              {'function OptanonWrapper() {}'}
            </Script>
          </>)}
      </ErrorBoundary>
    </>);
};
const __Next_Translate__Page__19f0049a6ce__ = App;

    export default __appWithI18n(__Next_Translate__Page__19f0049a6ce__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: true,
      loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  